import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Cesium from 'cesium';
import * as THREE from 'three';
import { TilesRenderer } from '3d-tiles-renderer';
import { useProfile } from '../hooks/useProfile';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { useTranslation } from 'react-i18next';
import { CANVAS_CONSTANTS, computeVRange, drawProfile } from './profileCanvas.js';

const DEBUG = false;
const debugLog = (...args) => { if (DEBUG) console.log('[ProfileViewer]', ...args); };

/**
 * Extract and copy color data from a geometry color attribute.
 * Handles both interleaved and regular buffer attributes, with various array types.
 * @returns {{ colorsCopy: TypedArray|null, colorChannelCount: number, isFloatColor: boolean }}
 */
const extractColorBuffer = (colorAttribute) => {
  if (!colorAttribute) {
    return { colorsCopy: null, colorChannelCount: 0, isFloatColor: false };
  }

  const colorChannelCount = colorAttribute.itemSize || 3;

  // Helper to create appropriate typed array and determine if float
  const createColorArray = (sourceArray, length) => {
    if (sourceArray instanceof Float32Array) {
      return { array: new Float32Array(length), isFloat: true };
    }
    if (sourceArray instanceof Uint8Array || sourceArray instanceof Uint8ClampedArray) {
      return { array: new Uint8Array(length), isFloat: false };
    }
    // Fallback: convert to float
    return { array: new Float32Array(length), isFloat: true };
  };

  // Interleaved buffer (stride/offset layout)
  if (colorAttribute.isInterleavedBufferAttribute && colorAttribute.data?.array) {
    const sourceColors = colorAttribute.data.array;
    const stride = colorAttribute.data.stride || colorChannelCount;
    const offset = colorAttribute.offset || 0;
    const colorCount = colorAttribute.count || 0;

    const { array: colorsCopy, isFloat } = createColorArray(sourceColors, colorCount * colorChannelCount);

    for (let i = 0; i < colorCount; i++) {
      const srcIndex = i * stride + offset;
      const dstIndex = i * colorChannelCount;
      for (let c = 0; c < colorChannelCount; c++) {
        colorsCopy[dstIndex + c] = sourceColors[srcIndex + c];
      }
    }

    return { colorsCopy, colorChannelCount, isFloatColor: isFloat };
  }

  // Regular buffer attribute
  if (colorAttribute.array) {
    const sourceColors = colorAttribute.array;
    const { array: colorsCopy, isFloat } = createColorArray(sourceColors, sourceColors.length);

    if (sourceColors instanceof Float32Array || sourceColors instanceof Uint8Array || sourceColors instanceof Uint8ClampedArray) {
      colorsCopy.set(sourceColors);
    } else {
      // Manual copy for other array types
      for (let i = 0; i < sourceColors.length; i++) {
        colorsCopy[i] = sourceColors[i];
      }
    }

    return { colorsCopy, colorChannelCount, isFloatColor: isFloat };
  }

  return { colorsCopy: null, colorChannelCount: 0, isFloatColor: false };
};

const ENDPOINT_TOLERANCE = 1e-3;
const HOVER_RADIUS_PX = 10;

const CAMERA_SETTINGS = {
  fovDegrees: 45,
  minDistance: CANVAS_CONSTANTS.CAMERA_HEIGHT_OFFSET,
  maxDistance: CANVAS_CONSTANTS.CAMERA_HEIGHT_OFFSET * 20,
  spanPadding: 1.1,
};

const HALF_FOV_TAN = Math.tan(THREE.MathUtils.degToRad(CAMERA_SETTINGS.fovDegrees) / 2);

const getCameraDistance = (basis) => {
  if (!basis || !Number.isFinite(HALF_FOV_TAN) || HALF_FOV_TAN <= 0) {
    return CAMERA_SETTINGS.minDistance;
  }

  const { length = 0, halfWidth = 0 } = basis;
  const span = Math.max(length * 0.5, halfWidth);
  if (!Number.isFinite(span) || span <= 0) {
    return CAMERA_SETTINGS.minDistance;
  }

  const paddedSpan = span * CAMERA_SETTINGS.spanPadding;
  const rawDistance = paddedSpan / HALF_FOV_TAN;
  if (!Number.isFinite(rawDistance) || rawDistance <= 0) {
    return CAMERA_SETTINGS.minDistance;
  }

  return Math.min(CAMERA_SETTINGS.maxDistance, Math.max(CAMERA_SETTINGS.minDistance, rawDistance));
};

/**
 * ProfileViewer Component
 * 
 * Renders a 2D elevation profile visualization for a line segment in 3D space.
 * This component manages the streaming of 3D tiles point cloud data, computes
 * elevation profiles using a web worker, and provides an interactive 2D plot
 * with pan/zoom capabilities.
 * 
 * @param {string} token - Cesium Ion access token
 * @param {string|number} tilesetId - Cesium Ion tileset ID
 * @param {Object} profile - Profile configuration with model geometry
 * @param {Function} onReady - Callback when profile data is ready
 */

/**
 * Creates and configures a TilesRenderer for Cesium Ion assets
 * 
 * @param {string} assetId - Cesium Ion asset ID
 * @param {string} ionAccessToken - Access token for authentication
 * @param {THREE.Camera} threeCamera - Three.js camera for SSE calculations
 * @param {THREE.WebGLRenderer} threeRenderer - Three.js renderer for SSE
 * @returns {Promise<TilesRenderer>} Configured TilesRenderer instance
 */
async function makeIonTilesRenderer(assetId, ionAccessToken, threeCamera, threeRenderer) {
  const endpointUrl = new URL(`https://api.cesium.com/v1/assets/${assetId}/endpoint`);
  endpointUrl.searchParams.append('access_token', ionAccessToken);

  debugLog('Fetching Ion endpoint', { assetId, url: endpointUrl.toString() });
  
  try {
    const endpointResponse = await fetch(endpointUrl, { mode: 'cors' });
    const endpointData = await endpointResponse.json();
    debugLog('Ion endpoint response', { hasUrl: !!endpointData?.url, hasToken: !!endpointData?.accessToken });
    
    const tilesetUrl = new URL(endpointData.url);
    const tilesetVersion = tilesetUrl.searchParams.get('v');

    const tilesRenderer = new TilesRenderer(tilesetUrl);
    tilesRenderer.fetchOptions = { headers: { Authorization: `Bearer ${endpointData.accessToken}` } };
    tilesRenderer.preprocessURL = uri => {
      const processedUrl = new URL(uri);
      processedUrl.searchParams.append('v', tilesetVersion);
      return processedUrl.toString();
    };
    
    tilesRenderer.setCamera(threeCamera);
    tilesRenderer.setResolutionFromRenderer(threeCamera, threeRenderer);
    tilesRenderer.displayActiveTiles = true;
    
    configureDracoLoader(tilesRenderer);
    debugLog('TilesRenderer initialized', { root: tilesetUrl.toString(), version: tilesetVersion });
    
    return tilesRenderer;
  } catch (error) {
    console.error('Failed to initialize Ion tiles renderer:', error);
    throw error;
  }
}

/**
 * Configures DRACO loader for compressed point cloud tiles
 * 
 * @param {TilesRenderer} tilesRenderer - The tiles renderer to configure
 */
function configureDracoLoader(tilesRenderer) {
  try {
    const dracoLoader = new DRACOLoader(tilesRenderer.manager);
    const DRACO_CDN = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';
    const DRACO_LOCAL = '/libs/draco/';
    const useLocalDraco = false;
    
    dracoLoader.setDecoderPath(useLocalDraco ? DRACO_LOCAL : DRACO_CDN);
    dracoLoader.setDecoderConfig({ type: 'wasm' });
    dracoLoader.preload();
    tilesRenderer.manager.addHandler(/\.drc$/i, dracoLoader);
    debugLog('DRACO configured', { path: useLocalDraco ? DRACO_LOCAL : DRACO_CDN });
  } catch (error) {
    console.warn('Failed to configure DRACO loader. Draco-compressed PNTS may not load.', error);
  }
}

// React component: renders a 2D elevation profile for a line segment while
// streaming a 3D tiles point cloud in the background for data sampling.
// Rendering is done on a canvas to avoid React re-renders; heavy sampling is
// offloaded to a Web Worker to keep the UI thread responsive.
function ProfileViewer({ token, tilesetId, profile, onReady, onDataRange }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const { t } = useTranslation();

  // Three.js hidden scene
  const threeRef = useRef({ renderer: null, camera: null, scene: null, tiles: null, raf: 0 });

  // Active 3D points currently loaded by tiles
  const activePointsRef = useRef(new Map()); // key: object.uuid -> THREE.Points

  // Current collected data for drawing
  const needsRedrawRef = useRef(false);
  const computeJobRef = useRef({ raf: 0 });
  const workerRef = useRef(null);
  const coloredRef = useRef({ u: null, v: null, rgb: null, w: null, n: 0 });

  // Incremental streaming: per-tile results map and batch counter
  const tileResultsRef = useRef(new Map()); // Map<tileUuid, {u, v, rgb, w, count}>
  const tileBatchMapRef = useRef(new Map()); // Map<tileUuid, batchId> — tracks freshness
  const batchIdRef = useRef(0);
  const lastReportedAspectRef = useRef(0); // throttle onDataRange calls
  const [hoverInfo, setHoverInfo] = useState(null);

  // Pan / zoom state for 2D canvas
  const dragRef = useRef({ dragging: false, x: 0, y: 0 });
  const transformRef = useRef({ scale: 1, dx: 0, dy: 0 });
  const resizeObserverRef = useRef(null);
  const metricsRef = useRef({ plotW: 1, plotH: 1, umin: 0, umax: 1, vmin: 0, vmax: 1, unitScale: 1 });
  const centerPendingRef = useRef(true);


  /**
   * Computes the current data ranges (min/max) for U and V coordinates
   * Used for axis scaling and plot bounds calculation
   * 
   * @returns {Object|null} Range object with umin, umax, vmin, vmax properties
   */
  const getRanges = () => {
    const currentBasis = basisRef.current;
    if (!currentBasis) return null;

    const { v: vCoordinates, n: pointCount } = coloredRef.current || {};
    const { vMin, vMax } = computeVRange(vCoordinates, pointCount || 0);

    return { umin: 0, umax: currentBasis.length, vmin: vMin, vmax: vMax };
  };

  /**
   * Centers the profile content within the plot area based on current ranges and zoom
   * Automatically adjusts the transform to show all data centered in the plot
   */
  const centerContent = () => {
    const canvas = canvasRef.current;
    const currentBasis = basisRef.current;
    if (!canvas || !currentBasis) return;
    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const plotWidth = Math.max(1, canvasWidth - CANVAS_CONSTANTS.MARGINS.L - CANVAS_CONSTANTS.MARGINS.R);
    const plotHeight = Math.max(1, canvasHeight - CANVAS_CONSTANTS.MARGINS.T - CANVAS_CONSTANTS.MARGINS.B);
    
    const ranges = getRanges();
    if (!ranges) return;
    
    const { umin, umax, vmin, vmax } = ranges;
    const unitScale = Math.min(plotWidth / (umax - umin), plotHeight / (vmax - vmin));
    const transform = transformRef.current;
    const scaledUnit = transform.scale * unitScale;
    const contentWidth = scaledUnit * (umax - umin);
    const contentHeight = scaledUnit * (vmax - vmin);
    
    transform.dx = (plotWidth - contentWidth) / 2;
    transform.dy = -(plotHeight - contentHeight) / 2;
    
    debugLog('centerContent', { plotWidth, plotHeight, contentWidth, contentHeight, dx: transform.dx, dy: transform.dy });
    needsRedrawRef.current = true;
  };

  // Subscribe to profile store so changes to width/points propagate while open
  const { profiles } = useProfile();
  const currentProfile = useMemo(() => {
    if (!profile) return null;
    const p = profiles?.find(it => it.id === profile.id);
    return p || profile;
  }, [profiles, profile]);

  // Basis computed from the current profile
  const basisRef = useRef(null);
  const prevProfileRef = useRef({ id: null, p0: null, p1: null, width: null });

  /**
   * Computes the local coordinate system basis for the profile
   * 
   * From two 3D points and width, computes:
   * - Tangential axis (u): along the line in the tangent plane
   * - Lateral axis (v): perpendicular to the line in the tangent plane
   * - Vertical axis (z): geodetic surface normal
   * - Profile dimensions and reference height
   * 
   * @param {Object} profileModel - Model with point1, point2, and width properties
   * @returns {Object|null} Basis object or null if invalid input
   */
  function computeBasis(profileModel) {
    if (!profileModel?.point1 || !profileModel?.point2) return null;
    
    const startPoint = profileModel.point1;
    const endPoint = profileModel.point2;
    const directionVector = Cesium.Cartesian3.subtract(endPoint, startPoint, new Cesium.Cartesian3());
    const profileLength = Cesium.Cartesian3.magnitude(directionVector);
    
    if (profileLength === 0) return null;
    
    const normalizedDirection = Cesium.Cartesian3.normalize(directionVector, new Cesium.Cartesian3());
    const geodeticNormal = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(startPoint, new Cesium.Cartesian3());
    
    const tangentialDirection = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.subtract(
        normalizedDirection, 
        Cesium.Cartesian3.multiplyByScalar(
          geodeticNormal, 
          Cesium.Cartesian3.dot(normalizedDirection, geodeticNormal), 
          new Cesium.Cartesian3()
        ), 
        new Cesium.Cartesian3()
      ),
      new Cesium.Cartesian3()
    );
    
    const lateralDirection = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.cross(geodeticNormal, tangentialDirection, new Cesium.Cartesian3()), 
      new Cesium.Cartesian3()
    );
    
    const profileHalfWidth = (profileModel.width || 1) * 0.5;
    const startCartographic = Cesium.Cartographic.fromCartesian(startPoint);
    const baseHeight = startCartographic.height || 0;

    if (DEBUG) {
      const endCartographic = Cesium.Cartographic.fromCartesian(endPoint);
      debugLog('computeBasis', {
        length: profileLength,
        width: profileModel.width,
        halfWidth: profileHalfWidth,
        startPoint: { x: startPoint.x, y: startPoint.y, z: startPoint.z },
        endPoint: { x: endPoint.x, y: endPoint.y, z: endPoint.z },
        startCartographic: {
          lonDeg: Cesium.Math.toDegrees(startCartographic.longitude),
          latDeg: Cesium.Math.toDegrees(startCartographic.latitude),
          height: startCartographic.height
        },
        endCartographic: {
          lonDeg: Cesium.Math.toDegrees(endCartographic.longitude),
          latDeg: Cesium.Math.toDegrees(endCartographic.latitude),
          height: endCartographic.height
        },
        tangential: { x: tangentialDirection.x, y: tangentialDirection.y, z: tangentialDirection.z },
        lateral: { x: lateralDirection.x, y: lateralDirection.y, z: lateralDirection.z },
        vertical: { x: geodeticNormal.x, y: geodeticNormal.y, z: geodeticNormal.z },
      });
    }
    
    return { 
      p0: startPoint, 
      p1: endPoint, 
      u: tangentialDirection, 
      v: lateralDirection, 
      z: geodeticNormal, 
      length: profileLength, 
      halfWidth: profileHalfWidth, 
      h0: baseHeight 
    };
  }


  /**
   * Handles profile rendering by delegating to the external drawing function
   */
  const handleDrawProfile = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      drawProfile(canvas, basisRef, coloredRef, transformRef, metricsRef, centerPendingRef, t, debugLog);
    }
  };

  /**
   * Merges all per-tile results from tileResultsRef into a single flat
   * coloredRef structure that drawProfile can consume.
   */
  function rebuildColoredFromMap() {
    const map = tileResultsRef.current;
    let totalCount = 0;
    map.forEach(entry => { totalCount += entry.count; });

    if (totalCount === 0) {
      coloredRef.current = { u: null, v: null, rgb: null, w: null, n: 0 };
      needsRedrawRef.current = true;
      return;
    }

    const u = new Float32Array(totalCount);
    const v = new Float32Array(totalCount);
    const rgb = new Uint8Array(totalCount * 3);
    const w = new Float32Array(totalCount * 3);
    let offset = 0;

    map.forEach(entry => {
      if (entry.count === 0) return;
      u.set(entry.u, offset);
      v.set(entry.v, offset);
      rgb.set(entry.rgb, offset * 3);
      w.set(entry.w, offset * 3);
      offset += entry.count;
    });

    coloredRef.current = { u, v, rgb, w, n: totalCount };
    needsRedrawRef.current = true;

    // Report data ranges so the parent can adapt the window size.
    // Only fire when the aspect ratio changes by >10% to avoid excessive updates.
    if (typeof onDataRange === 'function' && basisRef.current) {
      const { vMin, vMax } = computeVRange(v, totalCount);
      const elevationSpan = vMax - vMin;
      if (elevationSpan > 0 && isFinite(elevationSpan)) {
        const aspect = basisRef.current.length / elevationSpan;
        const prev = lastReportedAspectRef.current;
        if (prev === 0 || Math.abs(aspect - prev) / prev > 0.1) {
          lastReportedAspectRef.current = aspect;
          onDataRange({ length: basisRef.current.length, elevationSpan });
        }
      }
    }
  }

  /**
   * Ensures the web worker is initialized. Returns the worker instance.
   */
  function ensureWorker() {
    if (workerRef.current) return workerRef.current;

    try {
      workerRef.current = new Worker(new URL('./profilePairs.worker.js', import.meta.url));
      workerRef.current.onmessage = (event) => {
        const data = event.data;
        if (!data) return;

        // ── Streaming per-tile result ──
        if (data.type === 'tile-result') {
          // Guard against stale batches
          if (data.batchId !== batchIdRef.current) return;
          // Guard against disposed tiles
          if (!activePointsRef.current.has(data.tileId)) return;

          const { tileId, u, v, rgb, w, count } = data;
          tileResultsRef.current.set(tileId, { u, v, rgb, w, count });
          tileBatchMapRef.current.set(tileId, data.batchId);
          rebuildColoredFromMap();

          if (typeof onReady === 'function') {
            try { onReady(); } catch (error) {
              console.warn('Error calling onReady callback:', error);
            }
          }
          debugLog('tile-result', { tileId, count, totalTiles: tileResultsRef.current.size });
          return;
        }

        // ── Batch complete: prune stale entries from previous batches ──
        if (data.type === 'batch-complete') {
          const completedBatch = data.batchId;
          debugLog('batch-complete', { batchId: completedBatch });
          // Only act on the current batch (ignore late completions from old batches)
          if (completedBatch !== batchIdRef.current) return;

          let pruned = 0;
          tileBatchMapRef.current.forEach((entryBatch, tileId) => {
            if (entryBatch < completedBatch) {
              tileResultsRef.current.delete(tileId);
              tileBatchMapRef.current.delete(tileId);
              pruned++;
            }
          });
          if (pruned > 0) {
            debugLog('batch-complete pruned stale tiles', { pruned, remaining: tileResultsRef.current.size });
            rebuildColoredFromMap();
          }
          return;
        }

      };
    } catch (error) {
      console.warn('Failed to initialize profile worker:', error);
      return null;
    }
    return workerRef.current;
  }

  /**
   * Packages and sends a single THREE.Points object to the worker
   * for incremental profile computation.
   */
  function sendTileToWorker(pointObject) {
    const currentBasis = basisRef.current;
    const tilesRenderer = threeRef.current.tiles;
    if (!currentBasis || !tilesRenderer) return;

    const worker = ensureWorker();
    if (!worker) return;

    if (!pointObject?.isPoints || !pointObject.geometry) return;
    const positionAttribute = pointObject.geometry.getAttribute('position');
    if (!positionAttribute?.array) return;

    const tilesGroup = tilesRenderer.group;
    tilesGroup.updateMatrixWorld(true);
    pointObject.updateMatrixWorld(true);

    const sourcePositions = positionAttribute.array;
    const positionsCopy = new Float32Array(sourcePositions.length);
    positionsCopy.set(sourcePositions);

    const combinedMatrix = new THREE.Matrix4();
    combinedMatrix.copy(tilesGroup.matrixWorldInverse).multiply(pointObject.matrixWorld);
    const transformMatrix = new Float32Array(combinedMatrix.elements);

    const colorAttribute = pointObject.geometry.getAttribute('color');
    const { colorsCopy, colorChannelCount, isFloatColor } = extractColorBuffer(colorAttribute);

    const transferableBuffers = [positionsCopy.buffer];
    if (colorsCopy) transferableBuffers.push(colorsCopy.buffer);

    const basisPayload = {
      p0: [currentBasis.p0.x, currentBasis.p0.y, currentBasis.p0.z],
      u: [currentBasis.u.x, currentBasis.u.y, currentBasis.u.z],
      v: [currentBasis.v.x, currentBasis.v.y, currentBasis.v.z],
      z: [currentBasis.z.x, currentBasis.z.y, currentBasis.z.z],
      length: currentBasis.length,
      halfWidth: currentBasis.halfWidth,
      h0: currentBasis.h0,
    };

    try {
      worker.postMessage({
        type: 'compute-tile',
        batchId: batchIdRef.current,
        tileId: pointObject.uuid,
        basis: basisPayload,
        item: {
          positions: positionsCopy,
          matrix: transformMatrix,
          colors: colorsCopy,
          colorSize: colorChannelCount,
          colorIsFloat: isFloatColor,
        },
      }, transferableBuffers);
      debugLog('sendTileToWorker', { tileId: pointObject.uuid, points: positionAttribute.count });
    } catch (error) {
      console.warn('Failed to post tile to worker:', error);
    }
  }

  /**
   * Recomputes all tiles: increments batchId to invalidate stale results
   * and re-sends every active tile to the worker.
   * Old results are kept visible until replaced by fresh ones; stale
   * leftovers are pruned when the batch-complete message arrives.
   */
  function recomputeAllTiles() {
    const currentBasis = basisRef.current;
    const tilesRenderer = threeRef.current.tiles;
    if (!currentBasis || !tilesRenderer) return;

    batchIdRef.current++;
    lastReportedAspectRef.current = 0; // reset so the next data triggers onDataRange
    // Do NOT clear tileResultsRef — old data stays visible until overwritten

    if (activePointsRef.current.size === 0) {
      tileResultsRef.current.clear();
      tileBatchMapRef.current.clear();
      coloredRef.current = { u: null, v: null, rgb: null, w: null, n: 0 };
      needsRedrawRef.current = true;
      return;
    }

    activePointsRef.current.forEach((pointObject) => {
      sendTileToWorker(pointObject);
    });

    debugLog('recomputeAllTiles', { batchId: batchIdRef.current, tiles: activePointsRef.current.size });
  }

  /**
   * Main useEffect for setting up 3D tiles renderer and canvas
   * 
   * One-time setup that:
   * - Creates Three.js renderer/camera and TilesRenderer
   * - Sets up canvas element with resize handling
   * - Wires load/unload handlers to maintain active point set
   * - Initiates worker-based pairs recompute when tiles change
   */
  useEffect(() => {
    if (!containerRef.current) return;

    const three = threeRef.current;
    let tileEventHandlers = null;
    // Create hidden WebGL renderer
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.setSize(2048, 2048, false); // larger virtual resolution for finer SSE → higher tile detail
    const camera = new THREE.PerspectiveCamera(CAMERA_SETTINGS.fovDegrees, 1, 0.1, 1e8);
    const scene = new THREE.Scene();
    three.renderer = renderer;
    three.camera = camera;
    three.scene = scene;

    let disposed = false;

    /**
     * Synchronizes the Three.js camera with the tiles group coordinate frame
     * Positions the camera above the profile center for proper tile streaming
     */
    const syncCameraToTilesGroup = () => {
      try {
        const tilesRenderer = threeRef.current.tiles;
        const currentBasis = basisRef.current;
        if (!tilesRenderer || !currentBasis) return;
        
        const tilesGroup = tilesRenderer.group;
        tilesGroup.updateMatrixWorld(true);

        const profileCenter = Cesium.Cartesian3.midpoint(currentBasis.p0, currentBasis.p1, new Cesium.Cartesian3());
        const upVector = currentBasis.z;
        const cameraDistance = getCameraDistance(currentBasis);
        const cameraPositionECEF = Cesium.Cartesian3.add(
          profileCenter, 
          Cesium.Cartesian3.multiplyByScalar(upVector, cameraDistance, new Cesium.Cartesian3()), 
          new Cesium.Cartesian3()
        );

        const cesiumToThree = (cesiumVector) => new THREE.Vector3(cesiumVector.x, cesiumVector.y, cesiumVector.z);

        const cameraPositionThree = cesiumToThree(cameraPositionECEF).applyMatrix4(tilesGroup.matrixWorld);
        const lookAtPositionThree = cesiumToThree(profileCenter).applyMatrix4(tilesGroup.matrixWorld);

        const rotationMatrix = new THREE.Matrix3().setFromMatrix4(tilesGroup.matrixWorld);
        const upVectorThree = new THREE.Vector3(upVector.x, upVector.y, upVector.z).applyMatrix3(rotationMatrix).normalize();

        three.camera.position.copy(cameraPositionThree);
        three.camera.up.copy(upVectorThree);
        three.camera.lookAt(lookAtPositionThree);
        three.camera.updateProjectionMatrix();
        debugLog('syncCameraToTilesGroup', { cameraPositionThree, lookAtPositionThree, upVectorThree, cameraDistance });
      } catch (error) {
        console.warn('Failed to sync camera to tiles group:', error);
      }
    };

    /**
     * Initializes the 3D tiles renderer and sets up event handlers
     * @returns {Promise<void>}
     */
    async function initTiles() {
      try {
        const tiles = await makeIonTilesRenderer(tilesetId, token, camera, renderer);
        three.tiles = tiles;
        tiles.errorTarget = 1;                 // pixels; smaller => higher detail
        tiles.lruCache.maxSize = 5000;
        tiles.lruCache.minSize = 2000;
        tiles.lruCache.unloadPercent = 0.2;
        scene.add(tiles.group);

        debugLog('Tiles group added to scene');

        const onLoad = ({ scene: tileScene }) => {
          let pointsCount = 0;
          let meshes = 0;
          const newPoints = [];
          tileScene.traverse(obj => {
            if (obj.isPoints) {
              activePointsRef.current.set(obj.uuid, obj);
              newPoints.push(obj);
              const pos = obj.geometry?.getAttribute('position');
              pointsCount += pos ? pos.count : 0;
            } else if (obj.isMesh) {
              meshes++;
            }
          });
          debugLog('load-model', { tileChildren: tileScene.children?.length, collectedPointsObjects: activePointsRef.current.size, totalVertices: pointsCount, meshes });
          // Incrementally send only newly loaded tiles to the worker
          for (const obj of newPoints) {
            sendTileToWorker(obj);
          }
        };
        const onDispose = ({ scene: tileScene }) => {
          let removed = 0;
          tileScene.traverse(obj => {
            if (obj.isPoints) {
              activePointsRef.current.delete(obj.uuid);
              tileResultsRef.current.delete(obj.uuid);
              tileBatchMapRef.current.delete(obj.uuid);
              removed++;
            }
          });
          debugLog('dispose-model', { removedPointObjects: removed, remaining: activePointsRef.current.size });
          // Rebuild from remaining cached results — no worker round-trip needed
          rebuildColoredFromMap();
        };
        // Store handlers for cleanup
        tileEventHandlers = { onLoad, onDispose };

        tiles.addEventListener('load-model', onLoad);
        tiles.addEventListener('dispose-model', onDispose);

        // Simple update loop to keep tiles streaming
        const loop = () => {
          if (disposed) return;
          tiles.update();
          // no visible rendering required
          if (needsRedrawRef.current) {
            needsRedrawRef.current = false;
            handleDrawProfile();
          }
          three.raf = requestAnimationFrame(loop);
        };
        // Position camera relative to tiles group frame
        syncCameraToTilesGroup();
        loop();
      } catch (err) {
        console.warn('ProfileViewer: failed to init tiles', err);
      }
    }

    initTiles();

    // 2D canvas element
    const c = document.createElement('canvas');
    c.style.width = '100%';
    c.style.height = '100%';
    c.style.display = 'block';
    if (containerRef.current) containerRef.current.appendChild(c);
    canvasRef.current = c;

    // Resize observer to sync canvas size
    /**
     * Handles canvas resizing and updates plot dimensions
     */
    const resize = () => {
      const el = containerRef.current;
      if (!el || !canvasRef.current) return;
      const rect = el.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(rect.width * dpr));
      const h = Math.max(1, Math.floor(rect.height * dpr));
      if (canvasRef.current.width !== w || canvasRef.current.height !== h) {
        canvasRef.current.width = w;
        canvasRef.current.height = h;
        debugLog('resize canvas', { cssW: rect.width, cssH: rect.height, w, h, dpr });
        // center content after size change
        try { centerContent(); } catch {}
        needsRedrawRef.current = true;
      }
    };

    if ('ResizeObserver' in window) {
      resizeObserverRef.current = new ResizeObserver(resize);
      try { resizeObserverRef.current.observe(containerRef.current); } catch {}
    } else {
      window.addEventListener('resize', resize);
      resizeObserverRef.current = { disconnect: () => window.removeEventListener('resize', resize) };
    }
    resize();
    // center on open (before first pairs) and draw
    centerPendingRef.current = true;
    centerContent();

    return () => {
      disposed = true;
      try { 
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      } catch (error) {
        console.warn('Error disconnecting resize observer:', error);
      }
      if (three.raf) cancelAnimationFrame(three.raf);
      try {
        // remove event listeners before disposing tiles
        if (three.tiles && tileEventHandlers) {
          try { 
            three.tiles.removeEventListener('load-model', tileEventHandlers.onLoad); 
            three.tiles.removeEventListener('dispose-model', tileEventHandlers.onDispose); 
          } catch (error) {
            console.warn('Error removing tile event listeners:', error);
          }
        }
      } catch {}
      try { 
        if (computeJobRef.current.raf) {
          cancelAnimationFrame(computeJobRef.current.raf);
        }
      } catch (error) {
        console.warn('Error canceling animation frame:', error);
      }
      try { 
        if (three.tiles && three.tiles.dispose) {
          three.tiles.dispose();
        }
      } catch (error) {
        console.warn('Error disposing tiles:', error);
      }
      activePointsRef.current.clear();
      tileResultsRef.current.clear();
      tileBatchMapRef.current.clear();
      three.tiles = null;
      if (canvasRef.current?.parentElement) {
        try { 
          canvasRef.current.parentElement.removeChild(canvasRef.current);
        } catch (error) {
          console.warn('Error removing canvas element:', error);
        }
      }
      canvasRef.current = null;
    };
  }, [token, tilesetId]);

  // Update camera and basis when profile changes
  // When profile definition changes: recompute the basis, place camera,
  // optionally preserve view if only the width changed, and recompute pairs.
  useEffect(() => {
    const three = threeRef.current;
    if (!currentProfile?.model || !three.camera) return;
    debugLog('profile changed', { id: currentProfile?.id, width: currentProfile?.model?.width });
    const b = computeBasis(currentProfile.model);
    basisRef.current = b;
    if (!b) return;

    // Determine if endpoints changed vs width-only change
    const prev = prevProfileRef.current;
    const sameId = prev.id === currentProfile.id;
    const sameEndpoints = sameId && prev.p0 && prev.p1 &&
      Math.abs(prev.p0.x - b.p0.x) < ENDPOINT_TOLERANCE && Math.abs(prev.p0.y - b.p0.y) < ENDPOINT_TOLERANCE && Math.abs(prev.p0.z - b.p0.z) < ENDPOINT_TOLERANCE &&
      Math.abs(prev.p1.x - b.p1.x) < ENDPOINT_TOLERANCE && Math.abs(prev.p1.y - b.p1.y) < ENDPOINT_TOLERANCE && Math.abs(prev.p1.z - b.p1.z) < ENDPOINT_TOLERANCE;
    const widthOnlyChange = sameId && sameEndpoints && prev.width !== currentProfile.model.width;

    // Place camera above the slice in ECEF first for logging,
    // then if tiles exist, resync into tiles group frame.
    const center = Cesium.Cartesian3.midpoint(b.p0, b.p1, new Cesium.Cartesian3());
    const z = b.z;
    const cameraDistance = getCameraDistance(b);
    const camPos = Cesium.Cartesian3.add(center, Cesium.Cartesian3.multiplyByScalar(z, cameraDistance, new Cesium.Cartesian3()), new Cesium.Cartesian3());
    three.camera.position.set(camPos.x, camPos.y, camPos.z);
    three.camera.up.set(z.x, z.y, z.z);
    three.camera.lookAt(center.x, center.y, center.z);
    three.camera.updateProjectionMatrix();
    debugLog('camera placed (ECEF)', { camPos, center, up: z, cameraDistance });

    // If tiles are ready, align camera to tiles group frame
    if (three.tiles) {
      const group = three.tiles.group;
      group.updateMatrixWorld(true);
      const toThree = (c) => new THREE.Vector3(c.x, c.y, c.z);
      const camObj = toThree(camPos).applyMatrix4(group.matrixWorld);
      const lookObj = toThree(center).applyMatrix4(group.matrixWorld);
      const rot = new THREE.Matrix3().setFromMatrix4(group.matrixWorld);
      const upObj = new THREE.Vector3(z.x, z.y, z.z).applyMatrix3(rot).normalize();
      three.camera.position.copy(camObj);
      three.camera.up.copy(upObj);
      three.camera.lookAt(lookObj);
      three.camera.updateProjectionMatrix();
      debugLog('camera placed (tiles frame)', { camObj, lookObj, upObj });
    }

    // If width changed only, preserve view by anchoring the plot center
    let anchor = null;
    if (widthOnlyChange && canvasRef.current) {
      const canvas = canvasRef.current;
      const W = canvas.width, H = canvas.height;
      const { L, R, T, B } = CANVAS_CONSTANTS.MARGINS;
      const plotW = Math.max(1, W - L - R);
      const plotH = Math.max(1, H - T - B);
      const xp = L + plotW / 2;
      const yp = T + plotH / 2;
      const tr = transformRef.current;
      const { umin, vmin, unitScale } = metricsRef.current;
      const S = tr.scale * unitScale;
      const up = umin + (xp - L - tr.dx) / S;
      const vp = vmin + ((H - B) + tr.dy - yp) / S;
      anchor = { up, vp };
      debugLog('width-only change: anchor center', anchor);
    }

    // Re-enable auto-centering unless this is a width-only tweak (which preserves the anchor)
    if (!widthOnlyChange) {
      centerPendingRef.current = true;
    }

    // Recompute all tiles from current active points
    recomputeAllTiles();

    // If preserving view, adjust dx,dy to keep the anchor at center with new ranges
    if (anchor && canvasRef.current) {
      const canvas = canvasRef.current;
      const W = canvas.width, H = canvas.height;
      const { L, R, T, B } = CANVAS_CONSTANTS.MARGINS;
      const plotW = Math.max(1, W - L - R);
      const plotH = Math.max(1, H - T - B);
      const xp = L + plotW / 2;
      const yp = T + plotH / 2;
      // compute new ranges from current data
      const { v, n } = coloredRef.current || {};
      const { vMin: vmin, vMax: vmax } = computeVRange(v, n || 0);
      const umin = 0, umax = b.length;
      const unitScale = Math.min(plotW / (umax - umin), plotH / (vmax - vmin));
      const tr = transformRef.current;
      const S2 = tr.scale * unitScale;
      tr.dx = xp - L - S2 * (anchor.up - umin);
      tr.dy = yp - (H - B) + S2 * (anchor.vp - vmin);
      debugLog('width-only change: adjusted dx,dy', { dx: tr.dx, dy: tr.dy, S2 });
      needsRedrawRef.current = true;
    }

    // Update prev snapshot
    prevProfileRef.current = { id: currentProfile.id, p0: b.p0, p1: b.p1, width: currentProfile.model.width };
  }, [
    currentProfile?.id,
    currentProfile?.model?.width,
    currentProfile?.model?.point1?.x,
    currentProfile?.model?.point1?.y,
    currentProfile?.model?.point1?.z,
    currentProfile?.model?.point2?.x,
    currentProfile?.model?.point2?.y,
    currentProfile?.model?.point2?.z,
  ]);

  /**
   * Sets up mouse interaction handlers for pan/zoom functionality
   * 
   * Handles:
   * - Wheel zoom with pivot at plot center
   * - Pan drag with left mouse button
   * - Double-click reset to default zoom/pan
   * - Transform clamping to keep content within bounds
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /**
     * Clamps the view transform to keep content within plot boundaries
     * Prevents panning beyond the data bounds
     */
    const clampTransform = () => {
      const { plotW, plotH, umin, umax, vmin, vmax, unitScale } = metricsRef.current;
      const tr = transformRef.current;
      const S = tr.scale * unitScale;
      const contentW = S * (umax - umin);
      const contentH = S * (vmax - vmin);

      // Horizontal clamp: keep content within [L, L+plotW]
      if (contentW <= plotW) {
        tr.dx = (plotW - contentW) / 2; // lock centered
      } else {
        const minDx = plotW - contentW; // negative
        const maxDx = 0;
        if (tr.dx < minDx) tr.dx = minDx;
        if (tr.dx > maxDx) tr.dx = maxDx;
      }

      // Vertical clamp: keep content within [T, T+plotH]
      if (contentH <= plotH) {
        tr.dy = -(plotH - contentH) / 2; // lock centered (negative dy moves up)
      } else {
        const minDy = 0;
        const maxDy = contentH - plotH; // positive upper bound
        if (tr.dy < minDy) tr.dy = minDy;
        if (tr.dy > maxDy) tr.dy = maxDy;
      }

      debugLog('clamp', { dx: tr.dx, dy: tr.dy, contentW, contentH, plotW, plotH });
    };

    const onWheel = (e) => {
      e.preventDefault();
      centerPendingRef.current = false; // user interaction stops auto-centering
      const { deltaY } = e;
      const canvas = canvasRef.current;
      const W = canvas.width, H = canvas.height;
      const { L, R, T, B } = CANVAS_CONSTANTS.MARGINS;
      const plotW = Math.max(1, W - L - R);
      const plotH = Math.max(1, H - T - B);
      // pivot at plot center to keep content centered while zooming
      const xp = L + plotW / 2;
      const yp = T + plotH / 2;

      // current transform and metrics
      const tr = transformRef.current;
      const { umin, vmin, unitScale } = metricsRef.current;
      const S = tr.scale * unitScale;

      // Invert mapping to get world at pivot
      const up = umin + (xp - L - tr.dx) / S;
      const vp = vmin + ((H - B) + tr.dy - yp) / S;

      // Apply zoom factor
      const zoomFactor = Math.exp(-deltaY * CANVAS_CONSTANTS.ZOOM.SENSITIVITY);
      tr.scale *= zoomFactor;
      tr.scale = Math.min(Math.max(tr.scale, CANVAS_CONSTANTS.ZOOM.MIN_SCALE), CANVAS_CONSTANTS.ZOOM.MAX_SCALE);
      const S2 = tr.scale * unitScale;

      // Recompute dx, dy so pivot maps to same screen point
      tr.dx = xp - L - S2 * (up - umin);
      tr.dy = yp - (H - B) + S2 * (vp - vmin);

      debugLog('wheel', { deltaY, scale: tr.scale, S, S2, up, vp });
      clampTransform();
      needsRedrawRef.current = true;
    };

    const onDown = (e) => {
      centerPendingRef.current = false; // user interaction stops auto-centering
      dragRef.current.dragging = true;
      dragRef.current.x = e.clientX;
      dragRef.current.y = e.clientY;
      canvas.style.cursor = 'grabbing';
      debugLog('drag start', { x: e.clientX, y: e.clientY });
    };
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = canvas.width / Math.max(1, rect.width);

      if (dragRef.current.dragging) {
        const tr = transformRef.current;
        const dx = (e.clientX - dragRef.current.x) * dpr;
        const dy = (e.clientY - dragRef.current.y) * dpr;
        dragRef.current.x = e.clientX;
        dragRef.current.y = e.clientY;
        tr.dx += dx;
        tr.dy += dy;
        debugLog('drag move', { dx: tr.dx, dy: tr.dy });
        clampTransform();
        needsRedrawRef.current = true;
      } else {
        // Hover: compute nearest point and update overlay
        try {
          const px = (e.clientX - rect.left) * dpr;
          const py = (e.clientY - rect.top) * dpr;
          const { umin, vmin, unitScale } = metricsRef.current || {};
          if (umin === undefined) { setHoverInfo(null); return; }
          const { L, R, T, B } = CANVAS_CONSTANTS.MARGINS;
          const xMin = L, xMax = canvas.width - R;
          const yMin = T, yMax = canvas.height - B;
          if (px < xMin || px > xMax || py < yMin || py > yMax) { setHoverInfo(null); return; }

          const S = transformRef.current.scale * unitScale;
          const uToX = (u) => L + transformRef.current.dx + S * (u - umin);
          const vToY = (v) => (canvas.height - B) + transformRef.current.dy - S * (v - vmin);
          const data = coloredRef.current || {};
          const U = data.u, V = data.v, W = data.w, N = data.n;
          if (!U || !V || !W || !N) { setHoverInfo(null); return; }
          let bestIdx = -1, bestD2 = Infinity;
          const R2 = HOVER_RADIUS_PX * HOVER_RADIUS_PX;
          for (let i = 0; i < N; i++) {
            const xi = uToX(U[i]);
            const yi = vToY(V[i]);
            const dxp = xi - px;
            const dyp = yi - py;
            const d2 = dxp * dxp + dyp * dyp;
            if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
          }
          if (bestIdx >= 0 && bestD2 <= R2) {
            const wx = W[3 * bestIdx + 0];
            const wy = W[3 * bestIdx + 1];
            const wz = W[3 * bestIdx + 2];
            const carto = Cesium.Cartographic.fromCartesian(new Cesium.Cartesian3(wx, wy, wz));
            const lat = Cesium.Math.toDegrees(carto.latitude);
            const lon = Cesium.Math.toDegrees(carto.longitude);
            const h = carto.height;
            setHoverInfo({ lat, lon, h });
          } else {
            setHoverInfo(null);
          }
        } catch { setHoverInfo(null); }
      }
    };
    const onUp = () => { dragRef.current.dragging = false; canvas.style.cursor = 'default'; debugLog('drag end'); };
    const onLeave = onUp;
    const onDbl = () => { transformRef.current = { scale: 1, dx: 0, dy: 0 }; centerPendingRef.current = true; debugLog('reset zoom/pan'); needsRedrawRef.current = true; };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('dblclick', onDbl);
    return () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('dblclick', onDbl);
    };
  }, []);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {hoverInfo && (
        <div style={{ position: 'absolute', top: 12, right: 14, background: 'rgba(50,50,50,0.6)', color: '#ccc', padding: '6px 8px', borderRadius: 4, fontSize: 12, lineHeight: 1.2, pointerEvents: 'none', zIndex: 2 }}>
          <div>Lat: {hoverInfo.lat.toFixed(6)}</div>
          <div>Lng: {hoverInfo.lon.toFixed(6)}</div>
          <div>H: {hoverInfo.h.toFixed(2)} m</div>
        </div>
      )}
    </div>
  );
}

ProfileViewer.propTypes = {
  token: PropTypes.string,
  tilesetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  profile: PropTypes.shape({
    id: PropTypes.string,
    model: PropTypes.shape({
      point1: PropTypes.object,
      point2: PropTypes.object,
      width: PropTypes.number,
    })
  }),
  onReady: PropTypes.func,
  onDataRange: PropTypes.func,
};

export default ProfileViewer;
